import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The two pages a person's own emailed links open — plan-014 GAP (the MAIL hand-off; owner answer 41):
 *
 *   /verify-email#<token>              "Confirm your email address"     → verifyPersonEmail
 *   /me/security/email-change#<token>  "Confirm your new email address" → C1's confirmMyEmailChange
 *
 * Opening the link changes nothing: the token is consumed only by a press of the button, so a mail scanner or a link
 * preview that fetches the page cannot spend it. No sign-in is asked for — the token is the capability, and the link
 * may be opened on another device. Every unusable link (used, expired, never issued) is one page, as the API gives
 * one answer for all three. After a success every cached read is refetched, so a signed-in console shows the address
 * as verified (or the new address) at once.
 *
 * Drawn with the Organization pages' blocks (`../account-admin/page/organization-page.tsx`) and tokens only, like the
 * operator-action confirmation page.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button } from "@wetalk/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { GLYPH } from "../../shell/glyph.js";
import { Notice } from "../account-admin/page/organization-page.js";
import "./mail-landing-gateway.js";

import "../account-admin/page/organization-page.css";
import { PublicFrame, PublicKicker } from "../auth/public-frame.js";

export interface MailLandingScreenProps {
  /** The token from the link's fragment, exactly as it arrived (never logged); empty for none. */
  readonly token: string;
}

function Page(props: { readonly title: string; readonly lede: string; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("core");
  return (
    // ADR 0056 (portal amendment): an emailed sign-in link lands on a way-in page, drawn in the public design's frame.
    <PublicFrame width="wide">
      <section className="op-page">
        <PublicKicker>{portalText("core", "mail_landing.your_wetalk_sign_in_509236")}</PublicKicker>
        <h1 className="op-title">{props.title}</h1>
        <p className="op-lede">{props.lede}</p>
        {props.children}
      </section>
    </PublicFrame>
  );
}

function LogInLink(props: { readonly label: string }): ReactNode {
  usePortalTranslation("core");
  return (
    <Link to="/log-in" className="btn btn-secondary">
      {props.label}
    </Link>
  );
}

function Unavailable(): ReactNode {
  usePortalTranslation("core");
  return (
    <Page title={portalText("core", "mail_landing.nothing_to_confirm_here_49d03f")} lede={portalText("core", "mail_landing.this_console_sends_no_email_so_it_has_no_link_to_confirm_open_the_ed7b03")}>
      <div className="op-block" />
    </Page>
  );
}

function Unusable(props: { readonly ask: string }): ReactNode {
  usePortalTranslation("core");
  return (
    <Page title={portalText("core", "mail_landing.this_link_cannot_be_used_fcb489")} lede={portalText("core", "mail_landing.it_was_already_used_it_expired_or_it_is_incomplete_nothing_was_ch_49eb4a", { value0: props.ask })}>
      <div className="op-block">
        <Notice tone="neutral" glyph={GLYPH.lockSimple} title={portalText("core", "mail_landing.your_sign_in_is_as_it_was_8ad5b8")} text={portalText("core", "mail_landing.an_emailed_link_only_works_once_and_only_for_a_limited_time_7d85de")} actions={<LogInLink label={portalText("core", "auth.log_in_f7c400")} />} />
      </div>
    </Page>
  );
}

function isUnusable(raised: unknown): boolean {
  return raised instanceof ApiError && raised.code === "not_found";
}

export function VerifyEmailScreen(props: MailLandingScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const verify = gateway.verifyEmailAddress;
  const confirm = useMutation({
    mutationFn: () => {
      if (verify === undefined) throw new Error("unavailable");
      return verify(props.token);
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (verify === undefined) return <Unavailable />;
  const ask = portalText("core", "mail_landing.log_in_and_ask_for_a_new_link_from_my_security_80d780");
  if (props.token === "" || (confirm.isError && isUnusable(confirm.error))) return <Unusable ask={ask} />;
  if (confirm.isSuccess) {
    return (
      <Page title={portalText("core", "mail_landing.thank_you_your_email_address_is_confirmed_0a87d2")} lede={portalText("core", "mail_landing.wetalk_can_now_reach_you_there_about_your_sign_in_your_organizati_749b82")}>
        <div className="op-block">
          <Notice tone="accent" glyph={GLYPH.checkCircle} title={portalText("core", "mail_landing.address_confirmed_409a1a")} text={portalText("core", "mail_landing.you_can_close_this_page_or_continue_to_wetalk_33feb8")} actions={<LogInLink label={portalText("core", "mail_landing.continue_to_wetalk_2effac")} />} />
        </div>
      </Page>
    );
  }
  return (
    <Page title={portalText("core", "mail_landing.confirm_your_email_address_724104")} lede={portalText("core", "mail_landing.press_the_button_to_confirm_that_this_address_is_yours_nothing_ha_cf9850")}>
      <div className="op-block">
        <Notice
          tone="accent"
          glyph={GLYPH.envelope}
          title={portalText("core", "mail_landing.this_address_is_mine_8a442b")}
          text={portalText("core", "mail_landing.wetalk_sends_your_sign_in_codes_security_notices_and_bills_to_it_bbca78")}
          actions={
            <Button variant="primary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>{portalText("core", "mail_landing.confirm_my_email_address_63e8f3")}</Button>
          }
        />
      </div>
      {confirm.isError ? (
        <p className="op-toast op-toast-problem" role="status">
          {apiProblemText(confirm.error, portalText("core", "mail_landing.the_address_could_not_be_confirmed_try_again_in_a_moment_b2664c"))}
        </p>
      ) : null}
      <div className="op-block">
        <Notice tone="neutral" glyph={GLYPH.question} title={portalText("core", "mail_landing.did_not_sign_up_for_wetalk_875e18")} text={portalText("core", "mail_landing.ignore_this_page_and_the_email_nobody_can_use_the_address_without_2f355c")} />
      </div>
    </Page>
  );
}

export function EmailChangeScreen(props: MailLandingScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const confirmIt = gateway.confirmEmailChange;
  const confirm = useMutation({
    mutationFn: () => {
      if (confirmIt === undefined) throw new Error("unavailable");
      return confirmIt(props.token);
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (confirmIt === undefined) return <Unavailable />;
  const ask = portalText("core", "mail_landing.ask_for_a_new_link_from_my_security_98ff4f");
  if (props.token === "" || (confirm.isError && isUnusable(confirm.error))) return <Unusable ask={ask} />;
  if (confirm.data !== undefined) {
    return (
      <Page title={portalText("core", "mail_landing.thank_you_your_email_address_is_changed_253060")} lede={portalText("core", "mail_landing.you_now_sign_in_with_we_told_your_previous_address_about_the_chan_32b649", { value0: confirm.data.email })}>
        <div className="op-block">
          <Notice tone="accent" glyph={GLYPH.checkCircle} title={portalText("core", "mail_landing.address_changed_31fab2")} text={confirm.data.email} actions={<LogInLink label={portalText("core", "mail_landing.continue_to_wetalk_2effac")} />} />
        </div>
      </Page>
    );
  }
  const taken = confirm.isError && confirm.error instanceof ApiError && confirm.error.code === "email_taken";
  return (
    <Page title={portalText("core", "mail_landing.confirm_your_new_email_address_f5ab53")} lede={portalText("core", "mail_landing.press_the_button_to_sign_in_with_this_address_from_now_on_nothing_a7ac29")}>
      <div className="op-block">
        <Notice
          tone="accent"
          glyph={GLYPH.envelope}
          title={portalText("core", "mail_landing.use_this_address_for_my_sign_in_98b100")}
          text={portalText("core", "mail_landing.your_previous_address_receives_a_notice_that_the_address_changed_77e5b2")}
          actions={
            <Button variant="primary" disabled={confirm.isPending || taken} onClick={() => confirm.mutate()}>{portalText("core", "mail_landing.confirm_the_new_address_dccfec")}</Button>
          }
        />
      </div>
      {confirm.isError ? (
        <p className="op-toast op-toast-problem" role="status">
          {taken ? portalText("core", "mail_landing.another_wetalk_sign_in_took_that_address_meanwhile_your_address_i_cdc857") : apiProblemText(confirm.error, portalText("core", "mail_landing.the_change_could_not_be_confirmed_try_again_in_a_moment_48142d"))}
        </p>
      ) : null}
      <div className="op-block">
        <Notice tone="neutral" glyph={GLYPH.shieldCheck} title={portalText("core", "mail_landing.did_not_ask_for_this_5b454c")} text={portalText("core", "mail_landing.ignore_this_page_your_address_stays_as_it_is_and_you_may_want_to_aac21b")} />
      </div>
    </Page>
  );
}
